import { DestroyRef, Injectable, inject } from '@angular/core';

import { CallOffService } from '@routes/call-off/call-off.service';
import { InquiryService } from '@routes/inquiry/inquiry.service';
import { InvoicesService } from '@routes/invoices/invoices.service';
import { OrdersService } from '@routes/orders/orders.service';
import { ReadyForCallOffService } from '@routes/ready-for-call-off/ready-for-call-off.service';
import { ReportsService } from '@routes/reports/reports.service';
import { StockService } from '@routes/stock/stock.service';
import { UserAdminService } from '@routes/user-admin/user-admin.service';
import {
  Observable,
  combineLatest,
  distinctUntilChanged,
  filter,
  forkJoin,
  of,
  switchMap,
} from 'rxjs';
import { environment } from 'src/environments/environment';

import { NavService } from '@services/nav.service';

import { Paths } from '../app.routes';
import { ShellService } from '../components2/shell/shell.service';
import { UserService } from './user.service';

@Injectable({ providedIn: 'root' })
export class PreLoadService {
  shellService = inject(ShellService);
  navService = inject(NavService);
  userService = inject(UserService);

  ordersService = inject(OrdersService);
  inquiryService = inject(InquiryService);
  invoiceService = inject(InvoicesService);
  callOffService = inject(CallOffService);
  readyForCallOffService = inject(ReadyForCallOffService);
  stockService = inject(StockService);
  userAdminService = inject(UserAdminService);
  reportsService = inject(ReportsService);
  destroyRef = inject(DestroyRef);

  user$ = this.userService.user$;
  customerId$ = this.navService.customerId$;
  sourceSystemCodes$ = this.navService.sourceSystemCodes$;
  baseParams$ = this.navService.baseParams$;
  start$ = this.ordersService.ordersLoaded$;

  preLoad$: Observable<any[] | null> = combineLatest([
    this.start$,
    this.user$,
    this.baseParams$,
  ]).pipe(
    filter(([start]) => start),
    distinctUntilChanged(([start, user, baseParams], [start2, user2, baseParams2]) => {
      return (
        (start || start2) &&
        user?.id === user2?.id &&
        baseParams.customerId === baseParams2.customerId
      );
    }),
    switchMap(([start, user, baseParams]) => {
      if (!user || !baseParams) return of([]);

      if (environment.debug) {
        console.log('Start preloading', { user, start });
      }
      const navItems = this.shellService.getAllowedNavigationItems(user, baseParams.customerId);
      console.log(navItems);

      const observables = navItems.reduce((acc: Observable<any>[], item) => {
        if (item.id === Paths.order) {
          return [
            ...acc,
            this.ordersService.getOrders(baseParams, true),
            this.ordersService.getOrders(baseParams, false),
          ];
        }
        if (item.id === Paths.inquiry) {
          return [...acc, this.inquiryService.getInquiries(baseParams)];
        }
        if (item.id === Paths.invoices) {
          return [...acc, this.invoiceService.getInvoices(baseParams)];
        }
        if (item.id === Paths.callOff) {
          return [...acc, this.callOffService.getCallOffs(baseParams)];
        }
        if (item.id === Paths.readyForCallOff) {
          return [...acc, this.readyForCallOffService.getInwarehouseStock(baseParams)];
        }
        if (item.id === Paths.stockDetails) {
          return [...acc, this.stockService.getStockDetails(baseParams)];
        }

        if (item.id === Paths.reports) {
          return [...acc, this.reportsService.getOrdersWithQualityReports(baseParams)];
        }

        return acc;
      }, []);

      if (user?.isAdmin || user?.isCustomerService) {
        observables.push(this.userAdminService.getCustomers());
      }

      if (user?.isAdmin) {
        observables.push(this.userAdminService.getUsers());
      }
      return forkJoin(observables);
    }),
  );

  constructor() {
    if (environment.debug) {
      console.log('PreLoadService created');
    }
    /*
    this.preLoad$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (environment.debug) {
        console.log('Preloading done', result);
      }
    });
    */
  }
}
